import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, CalendarClock, CheckCircle2, CreditCard, ExternalLink, FileScan,
  Globe, Landmark, MonitorSmartphone, Receipt, Send, TriangleAlert, UserCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { prefectureSite } from "@/lib/prefecture";
import { RenewalChecklistButton } from "@/components/dashboard/RenewalChecklistButton";
import { EASE } from "@/components/landing/Reveal";

const ANEF_URL = "https://administration-etrangers-en-france.interieur.gouv.fr";
const TIMBRE_URL = "https://timbres.impots.gouv.fr";

const frDate = (d) => d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

const STEPS = [
  {
    icon: UserCheck,
    title: "Vérifiez que votre titre se renouvelle en ligne",
    body: "La grande majorité des titres (salarié, étudiant, visiteur, passeport talent, VLS-TS, carte pluriannuelle…) se renouvellent sur la plateforme ANEF. Quelques situations particulières (certains titres « vie privée et familiale ») passent encore par la préfecture : en cas de doute, consultez le site de votre préfecture.",
  },
  {
    icon: CalendarClock,
    title: "Respectez la fenêtre de dépôt",
    body: "La demande de renouvellement se dépose entre 4 mois et 2 mois avant l'expiration de votre titre. Un dépôt tardif peut entraîner une rupture de droits (travail, prestations) et un visa de régularisation de 180 €.",
  },
  {
    icon: MonitorSmartphone,
    title: "Créez ou connectez votre compte ANEF",
    body: "Rendez-vous sur administration-etrangers-en-france.interieur.gouv.fr puis « Je demande ou renouvelle un titre de séjour ». Munissez-vous de votre numéro étranger (numéro AGDREF à 10 chiffres, inscrit sur votre titre actuel) et d'une adresse email valide.",
  },
  {
    icon: FileScan,
    title: "Numérisez vos pièces justificatives",
    body: "Scannez ou photographiez chaque pièce (PDF ou JPEG lisible, moins de 10 Mo par fichier) : passeport, titre actuel, justificatif de domicile de moins de 6 mois, e-photo, et les pièces propres à votre situation. Utilisez votre checklist personnalisée ci-dessous pour ne rien oublier.",
    checklist: true,
  },
  {
    icon: Send,
    title: "Remplissez et déposez la demande en ligne",
    body: "Complétez le formulaire ANEF, téléversez vos pièces puis validez. Vous recevez une confirmation par email et une attestation de dépôt téléchargeable dans votre espace : conservez-la, elle prouve que votre demande est en cours.",
  },
  {
    icon: Receipt,
    title: "Payez le timbre fiscal",
    body: "Le montant dépend du titre (le plus souvent 225 € : taxe + droit de timbre). Achetez le timbre fiscal dématérialisé sur timbres.impots.gouv.fr au moment indiqué par l'ANEF — généralement lorsque le titre est fabriqué et mis à disposition.",
  },
  {
    icon: Landmark,
    title: "Suivez votre demande et retirez votre titre",
    body: "Suivez l'avancement dans votre espace ANEF. Si votre titre expire pendant l'instruction, une attestation de prolongation d'instruction (API) maintient vos droits. Vous serez ensuite convoqué en préfecture si nécessaire (empreintes) puis invité à retirer votre nouveau titre.",
  },
];

export default function GuideRenouvellement() {
  const { user } = useAuth();
  const expiry = user?.titre_expiry ? new Date(`${user.titre_expiry}T00:00:00`) : null;
  const expiryDays = expiry ? Math.ceil((expiry - new Date()) / 86400000) : null;
  const windowStart = expiry ? new Date(expiry.getTime() - 120 * 86400000) : null;
  const windowEnd = expiry ? new Date(expiry.getTime() - 60 * 86400000) : null;
  const prefUrl = prefectureSite(user?.departement);

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-guide-renouvellement">
      <div>
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-bleu" data-testid="guide-back-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Tableau de bord
        </Link>
        <h1 className="mt-2 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Renouveler mon titre en ligne (ANEF)</h1>
        <p className="mt-1 text-sm text-slate-500">
          Le guide pas à pas pour déposer votre demande de renouvellement sur la plateforme officielle
          ANEF — Administration Numérique pour les Étrangers en France.
        </p>
      </div>

      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
        className="card-integra p-5" data-testid="guide-situation-card">
        {user?.titre_type ? (
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bleu-light"><CreditCard className="h-5 w-5 text-bleu" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Votre situation</p>
              <p className="mt-0.5 text-sm font-bold text-slate-900">{user.titre_type}</p>
              {expiry && (
                <p className="mt-0.5 text-xs text-slate-500" data-testid="guide-window">
                  Expire le <strong>{frDate(expiry)}</strong>{expiryDays >= 0 ? ` (dans ${expiryDays} jour${expiryDays > 1 ? "s" : ""})` : ` (expiré depuis ${-expiryDays} jour${-expiryDays > 1 ? "s" : ""})`}.{" "}
                  {expiryDays !== null && expiryDays >= 0 && windowStart && (
                    expiryDays > 120
                      ? `Votre fenêtre de dépôt s'ouvrira le ${frDate(windowStart)}.`
                      : expiryDays >= 60
                        ? `Vous êtes dans la fenêtre idéale de dépôt (jusqu'au ${frDate(windowEnd)}).`
                        : "La fenêtre idéale est dépassée : déposez votre demande sans attendre."
                  )}
                  {expiryDays !== null && expiryDays < 0 && "Déposez votre demande immédiatement et rapprochez-vous de votre préfecture."}
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="flex items-start gap-2.5 text-xs leading-relaxed text-slate-600" data-testid="guide-no-titre-note">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-ambre" />
            <span>
              Renseignez votre titre de séjour (type et date d'expiration) dans{" "}
              <Link to="/dashboard/parametres" className="font-bold text-bleu hover:underline">Paramètres</Link>{" "}
              ou scannez-le avec l'<Link to="/dashboard/interprete" className="font-bold text-bleu hover:underline">Interprète IA</Link>{" "}
              pour personnaliser ce guide et recevoir vos rappels de renouvellement.
            </span>
          </p>
        )}
      </motion.div>

      <ol className="space-y-3">
        {STEPS.map((s, i) => (
          <motion.li key={s.title}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 + i * 0.06, ease: EASE }}
            className="card-integra flex gap-4 p-5" data-testid={`guide-step-${i + 1}`}>
            <div className="flex flex-col items-center">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bleu-light font-display text-sm font-extrabold text-bleu">{i + 1}</span>
              {i < STEPS.length - 1 && <span className="mt-2 w-px flex-1 bg-slate-100" />}
            </div>
            <div className="min-w-0 flex-1 pb-1">
              <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><s.icon className="h-4 w-4 shrink-0 text-bleu" /> {s.title}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{s.body}</p>
              {s.checklist && user?.titre_type && <RenewalChecklistButton className="mt-3" />}
            </div>
          </motion.li>
        ))}
      </ol>

      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5, ease: EASE }}
        className="card-integra p-5" data-testid="guide-links">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900"><Globe className="h-4 w-4 text-bleu" /> Liens officiels</h2>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <a href={ANEF_URL} target="_blank" rel="noreferrer" className="btn-secondary !justify-center !px-4 !py-2.5 !text-xs" data-testid="guide-link-anef">
            Plateforme ANEF <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <a href={TIMBRE_URL} target="_blank" rel="noreferrer" className="btn-secondary !justify-center !px-4 !py-2.5 !text-xs" data-testid="guide-link-timbre">
            Timbre fiscal <ExternalLink className="h-3.5 w-3.5" />
          </a>
          {prefUrl && (
            <a href={prefUrl} target="_blank" rel="noreferrer" className="btn-secondary !justify-center !px-4 !py-2.5 !text-xs" data-testid="guide-link-prefecture">
              Ma préfecture <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
        <p className="mt-3 flex items-start gap-2 text-[11px] leading-relaxed text-slate-400">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emeraude" />
          Vos rappels d'échéances (fenêtre de dépôt, rendez-vous) sont gérés automatiquement dans{" "}
          <Link to="/dashboard/echeances" className="font-bold text-bleu hover:underline">Mes échéances</Link>.
        </p>
      </motion.div>
    </div>
  );
}
